/**
 * Status utility functions for Warehouse module
 */

import type { VariantProps } from 'class-variance-authority'
import { badgeVariants } from '@/components/ui/badge'
import {
    EQUIPMENT_STATUS_VARIANTS,
    EQUIPMENT_STATUS_COLORS,
    RACK_STATUS_COLORS,
    RACK_STATUS_VARIANTS,
    ITEM_CONDITION_COLORS,
    ITEM_CONDITION_VARIANTS,
    ITEM_TYPE_COLORS,
    ITEM_TYPE_LABELS,
    PROCESS_STATUS_COLORS,
    PROCESS_TYPE_COLORS,
    PROCESS_TYPE_LABELS,
    LOCATION_TYPE_COLORS,
} from '../constants'

// Badge `variant` union, derived from the Badge component so it stays in sync
type BadgeVariant = VariantProps<typeof badgeVariants>['variant']

/**
 * Get a badge variant for a given status string.
 * Uses constant mapping for consistent styling.
 */
export const getStatusBadgeVariant = (status: string): BadgeVariant => {
    const normalized = status?.toLowerCase()
    return (EQUIPMENT_STATUS_VARIANTS[normalized] ?? 'outline') as BadgeVariant
}

/**
 * Get equipment status color for the Chip component
 */
export const getEquipmentStatusColor = (status: string): string => {
    const normalized = status?.toLowerCase()
    return EQUIPMENT_STATUS_COLORS[normalized] ?? 'default'
}

/**
 * Get rack status color for the Chip component
 */
export const getRackStatusColor = (status: string): string => {
    const normalized = status?.toLowerCase()
    return RACK_STATUS_COLORS[normalized] ?? 'default'
}

/**
 * Get rack status badge variant for a Badge component
 */
export const getRackStatusBadgeVariant = (status: string): BadgeVariant => {
    const normalized = status?.toLowerCase()
    return (RACK_STATUS_VARIANTS[normalized] ?? 'outline') as BadgeVariant
}

/**
 * Get condition badge variant for the Badge component
 */
export const getConditionBadgeVariant = (condition: string): BadgeVariant => {
    const normalized = condition?.toLowerCase()
    return (ITEM_CONDITION_VARIANTS[normalized] ?? 'outline') as BadgeVariant
}

/**
 * Get item condition color for the Chip component
 */
export const getConditionColor = (condition: string): string => {
    const normalized = condition?.toLowerCase()
    return ITEM_CONDITION_COLORS[normalized] ?? 'default'
}

/**
 * Get item type color for the Chip component
 */
export const getItemTypeColor = (type: string): string => {
    const normalized = type?.toLowerCase()
    return ITEM_TYPE_COLORS[normalized] ?? 'default'
}

/**
 * Get item type label for display
 */
export const getItemTypeLabel = (type: string): string => {
    return ITEM_TYPE_LABELS[type] ?? type
}

/**
 * Get process status color for the Chip component
 */
export const getProcessStatusColor = (status: string): string => {
    return PROCESS_STATUS_COLORS[status] ?? 'default'
}

/**
 * Get process type color for the Chip component
 */
export const getProcessTypeColor = (type: string): string => {
    return PROCESS_TYPE_COLORS[type] ?? 'default'
}

/**
 * Get a process type label for display
 */
export const getProcessTypeLabel = (type: string): string => {
    if (PROCESS_TYPE_LABELS[type]) {
        return PROCESS_TYPE_LABELS[type]
    }
    // Fallback: convert snake_case to Title Case
    return type
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

/**
 * Get location type color for the Chip component
 */
export const getLocationTypeColor = (type: string): string => {
    const normalized = type?.toLowerCase()
    return LOCATION_TYPE_COLORS[normalized] ?? 'default'
}
